import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { Select, MenuItem } from '@mui/material';
import { MigrationNoteType } from '@src/constants/MigrationNoteType';
import CardNoteDTO from '@src/adapters/dto/card/CardNoteDTO';
import { CardNoteType } from '@src/constants/CardNoteType';
import useSaveCardNoteLazy from '@src/adapters/hooks/card/useSaveCardNote';

interface Props {
  onClose(note: CardNoteDTO | undefined): void;
  cardId: string;
  note?: CardNoteDTO;
  isOpen: boolean;
  type?: CardNoteType;
}

function NoteDetailDialog({ onClose, cardId, isOpen, note, type }: Props) {
  const { execute: saveNote } = useSaveCardNoteLazy();

  const fixedType = useMemo(() => {
    if (type) {
      return type;
    }

    if (note) {
      return note.type;
    }

    return undefined;
  }, [note, type]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CardNoteDTO>();

  useEffect(() => {
    if (isOpen && !note && type) {
      reset({ type: type });
    } else {
      reset(note);
    }
  }, [isOpen, reset, note, type]);

  const onSubmit = async (note: CardNoteDTO) => {
    const result = await saveNote({
      note,
      cardId,
    });

    if (result.note) {
      reset();
      onClose(result.note);
    }
  };

  const handleClose = () => {
    reset();
    onClose(undefined);
  };

  return (
    <Dialog
      open={isOpen}
      aria-labelledby="Note Details"
      aria-describedby="Note Details"
      fullWidth={true}
      maxWidth={'md'}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h6">
          {`${note ? 'Update' : 'Add'} ${fixedType ? ` ${fixedType} ` : ' '}
            Note`}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}>
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Content"
                multiline
                sx={{
                  width: '100%',
                  marginTop: '20px',
                  marginBottom: '20px',
                }}
              />
            )}
          />
          {errors.content && <p>{errors.content.message}</p>}
          {!fixedType && (
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Type"
                  sx={{
                    width: '100%',
                    marginTop: '20px',
                    marginBottom: '20px',
                  }}>
                  <MenuItem value={MigrationNoteType.Scrub}>
                    Scrub Note
                  </MenuItem>
                  <MenuItem value={MigrationNoteType.General}>
                    General Note
                  </MenuItem>
                  <MenuItem value={MigrationNoteType.Migration}>
                    Migration Note
                  </MenuItem>
                </Select>
              )}
            />
          )}
          {!fixedType && errors.type && <p>{errors.type.message}</p>}
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            {note ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default NoteDetailDialog;
